import { SelectOption } from '@michelin/select-options-provider';
import i18n from '@michelin/central-provider/dist/translations/CentralTranslation';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import {
  Customer,
  DayWorkHours, DealerProfileLocation,
} from '../Types';
import { version } from '../version.json';
import config from '../global-variables';

export type TranslateCallback = typeof i18n.t;

const CANVAS_MODE_ADDITION = runningOnCanvas() ? 64 : 0;

export const CONTENT_AREA_HEIGHT = `calc(100vh - 152px + ${CANVAS_MODE_ADDITION}px)`;
export const FOOTERED_CONTENT_AREA_HEIGHT = `calc(100vh - 224px + ${CANVAS_MODE_ADDITION}px)`;
export const TABBED_CONTENT_HEIGHT = `calc(100vh - 200px + ${CANVAS_MODE_ADDITION}px)`;

export function getVersion() {
  const branch = config.env;
  const number = version.split('-').pop() || '';
  if (branch === number) return number;
  return `${number} (${branch.substring(0, 1).toLocaleUpperCase()})`;
}

function strSortLower(a?: string | null, b?: string | null) {
  // eslint-disable-next-line no-param-reassign
  a = a ? a.toLowerCase() : null;
  // eslint-disable-next-line no-param-reassign
  b = b ? b.toLowerCase() : null;
  if ((a || '') < (b || '')) return -1;
  if ((a || '') > (b || '')) return 1;
  return 0;
}

export function sortOptions(options: SelectOption[], keepFirstUnsorted: boolean): SelectOption[] {
  let first = null;
  if (keepFirstUnsorted) first = options.shift();
  options.sort((x, y) => strSortLower(x.label, y.label));
  if (keepFirstUnsorted && first) options.unshift(first);
  return options;
}

export function hasEmailFormat(email: string) {
  if (email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }
  return true;
}

function twoDigits(a: number): string {
  if (a < 10) return `0${a.toString()}`;
  return a.toString();
}

export function getPartialTimeString(time: string) {
  try {
    const parts = time.trim().split(':');
    if (parts.length < 2) return '';
    // eslint-disable-next-line radix
    const minutes = parseInt(parts[1]);
    // eslint-disable-next-line radix
    let hours = parseInt(parts[0]);
    let suffix = 'am';
    if (hours >= 12) {
      suffix = 'pm';
      if (hours > 12) hours -= 12;
    }
    return `${hours}:${twoDigits(minutes)} ${suffix}`;
  } catch (e) {
    return '';
  }
}

export function getTimeString(time?: DayWorkHours | null) {
  const begin: string = (time?.begin) || '';
  const end: string = (time?.end) || '';
  const suffix: string = begin && end && begin >= end ? ' (+1d)' : '';
  let result = `${getPartialTimeString(begin)} - ${getPartialTimeString(end)}${suffix}`;
  result = result.trim();
  if (result === '-') return '';
  return result;
}

export function scrollToDisplayElementById(id: string) {
  const elem = document.getElementById(id);
  if (!elem) return;
  try {
    // @ts-ignore
    elem.scrollIntoViewIfNeeded();
    // eslint-disable-next-line no-empty
  } catch (e) { }
}

export function scrollToDisplayFirstError() {
  const elem = document.getElementsByClassName('Mui-error')[0];
  if (!elem) return;
  try {
    // @ts-ignore
    elem.scrollIntoViewIfNeeded();
    // eslint-disable-next-line no-empty
  } catch (e) { }
}

export function setPageTitle(section: string | null | undefined) {
  window.document.title = `${section ? `${section} - ` : ''}Service Provider Profiles - Michelin`;
  // @ts-ignore
  if (window.onScroll) [5, 10, 50, 100, 500].forEach((t) => setTimeout(window.onScroll, t));
}

export function getCountryCode(l: DealerProfileLocation, c: Customer) {
  if (c?.customer_country) {
    return c.customer_country.toLowerCase();
  }
  if (l.mailing_country) return l.mailing_country.toLowerCase();
  return 'us';
}

export function getKeys<T>(object: T) {
  return Object.keys(object as {[key:string]:any}) as (keyof T)[];
}

export function getEntries<T>(object: T, sort?: boolean) {
  if (sort) return getKeys(object).sort().map((key) => ({ key, value: object[key] }));
  return getKeys(object).map((key) => ({ key, value: object[key] }));
}

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
